/**
 * Keyword matcher, shared by the Worker (which decides matches) and the client
 * (which highlights them), so both apply identical rules.
 *
 * Matching is case-insensitive and word-boundary aware, so "Shopee" hits
 * "Shopee's ranking model" but "cat" never hits "concatenate". Boundaries are
 * asserted with Unicode-aware lookarounds rather than \b, because \b is defined
 * against [A-Za-z0-9_] and would misbehave for terms that start or end in
 * punctuation ("C++", "GPT-4") or in non-Latin scripts.
 */

const WORD_CHAR = String.raw`[\p{L}\p{N}_]`;

/**
 * Scripts that do not separate words with spaces.
 *
 * Han/Kana/Hangul characters are all `\p{L}`, so asserting a word boundary
 * around them would make a term like 深度学习 unmatchable inside ordinary
 * Chinese text — its neighbours are always "word characters". For these the
 * term is matched as a plain substring instead.
 */
const SCRIPTLESS = /[\p{sc=Han}\p{sc=Hiragana}\p{sc=Katakana}\p{sc=Hangul}]/u;

export function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Collapses internal whitespace so a stored term always matches normalised text. */
export function normalizeTerm(term: string): string {
  return term.replace(/\s+/g, " ").trim();
}

export function buildMatcher(term: string): RegExp {
  const normalized = normalizeTerm(term);
  // Tolerate any run of whitespace (including newlines) between words.
  const body = escapeRegExp(normalized).replace(/ /g, String.raw`\s+`);

  const chars = Array.from(normalized);
  const first = chars[0] ?? "";
  const last = chars[chars.length - 1] ?? "";
  const isWordChar = new RegExp(`^${WORD_CHAR}$`, "u");

  // A boundary is only asserted on an edge that is a word character in a
  // space-delimited script. "C++" must be free to sit next to anything, and
  // CJK terms must be free to sit next to other CJK.
  const guardStart = isWordChar.test(first) && !SCRIPTLESS.test(first);
  const guardEnd = isWordChar.test(last) && !SCRIPTLESS.test(last);

  const leading = guardStart ? `(?<!${WORD_CHAR})` : "";
  // Tolerate a regular English plural on the final word, so "transformer"
  // finds "transformers" and "index" finds "indexes". The trailing boundary
  // still applies, so this cannot degrade into substring matching:
  // "cat" stays clear of "catalog", and "bus" of "business".
  const plural = guardEnd ? "(?:e?s)?" : "";
  const trailing = guardEnd ? `(?!${WORD_CHAR})` : "";

  return new RegExp(`${leading}${body}${plural}${trailing}`, "iu");
}

/**
 * Character ranges in `text` hit by any of `terms`, sorted and merged.
 *
 * Built on the very matcher that decided the match, so a highlight can never
 * disagree with the reason the paper was flagged — a looser client-side search
 * would light up "catalog" for "cat".
 */
export function highlightRanges(text: string, terms: string[]): [number, number][] {
  const ranges: [number, number][] = [];
  for (const term of terms) {
    if (normalizeTerm(term).length === 0) continue;
    const global = new RegExp(buildMatcher(term).source, "giu");
    for (const hit of text.matchAll(global)) {
      if (hit[0].length === 0) continue;
      ranges.push([hit.index, hit.index + hit[0].length]);
    }
  }
  ranges.sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const merged: [number, number][] = [];
  for (const range of ranges) {
    const last = merged[merged.length - 1];
    if (last && range[0] <= last[1]) last[1] = Math.max(last[1], range[1]);
    else merged.push([range[0], range[1]]);
  }
  return merged;
}

/** Splits text into plain and highlighted runs, for renderers on either side. */
export function highlightSegments(text: string, terms: string[]): { text: string; hit: boolean }[] {
  const segments: { text: string; hit: boolean }[] = [];
  let at = 0;
  for (const [start, end] of highlightRanges(text, terms)) {
    if (start > at) segments.push({ text: text.slice(at, start), hit: false });
    segments.push({ text: text.slice(start, end), hit: true });
    at = end;
  }
  if (at < text.length) segments.push({ text: text.slice(at), hit: false });
  return segments;
}
